/**
 * @file edit-user-modal.tsx
 * @description
 * -----------------------------------------------------
 * Modal para editar los datos de un usuario
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2026-04-01
 * @author DLTCode
 * @author Sergio Manjón
 * @copyright © DLTCode
 * @license Private
 * */
import ModalComponent from "@/components/modals/ModalComponent";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";
import SelectComponent from "@/components/ui/SelectComponent";
import { updateUserAdmin } from "../actions";
import { EditUserModalProps, UpdateUser } from "../types";
import ToggleComponent from "@/components/ui/ToggleComponent";
import { USER_ROLES } from "@/constants/roles";
const EditUserModal = ({
  isOpen,
  onClose,
  onClear,
  user
}: EditUserModalProps) => {
  const modals = useTranslations("Modals");
  const labels = useTranslations("Labels");
  const options = useTranslations("SelectComponent.Options");
  const [formData, setFormData] = useState<UpdateUser>({
    id: +user?.id || 0,
    mail: user?.email || "",
    name: user?.name || "",
    role: user?.role || "",
    twoFactorCode: user?.twoFactorCode || false
  });
  const [errors, setErrors] = useState<{
    [key: string]: string[];
  }>({});
  const handleChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const {
      id,
      value
    } = event.target;
    setFormData({
      ...formData,
      [id]: value
    });
    if (errors[id]) {
      setErrors(prevErrors => {
        const newErrors = {
          ...prevErrors
        };
        delete newErrors[id];
        return newErrors;
      });
    }
  };
  const handleToggleChange = () => {
    setFormData(prevData => ({
      ...prevData,
      twoFactorCode: !prevData.twoFactorCode
    }));
  };
  const handleSubmit = async (formData_0: FormData) => {
    try {
      const response = await updateUserAdmin(formData_0);
      if (response && "fieldErrors" in response) {
        setErrors(response.fieldErrors);
        return;
      }
      if (response && "error" in response) {
        toast.error(response.message);
        return;
      }
      toast.success(response.message);
      onClose();
      onClear();
    } catch (error) {
      if (error instanceof Error) {
        setErrors({
          mail: [error.message]
        });
      }
    }
  };
  const hasChanges = formData.role !== user?.role || formData.twoFactorCode !== user?.twoFactorCode;
  return <ModalComponent title={modals("editUser.title")} isOpen={isOpen} onClose={onClose} buttonCancel={true} formAction={handleSubmit} formData={{
    id: formData.id,
    mail: formData.mail,
    name: formData.name,
    role: formData.role,
    twoFactorCode: formData.twoFactorCode
  }} buttonSubmitText="buttonSaveChanges" hideSubmitIcon={true} disabledConfirm={!hasChanges}>
      <input type="hidden" name="id" value={formData.id} />
      <input type="hidden" name="twoFactorCode" value={formData.twoFactorCode ? "true" : "false"} />
      {/*
        Datos de solo lectura (mail y nombre) expuestos como lista de
        descripciones (`<dl>`) para asociar etiqueta↔valor de forma
        programática (WCAG 1.3.1) en lugar de inputs deshabilitados.
       */}
      <dl className="modal__details modal__details--static">
        <div className="modal__details__field">
          <dt id="edit-user-mail-label" className="label">
            {labels("labelMail")}
          </dt>
          <dd aria-labelledby="edit-user-mail-label" className="modal__details__value">
            {formData.mail || "-"}
          </dd>
        </div>
        <div className="modal__details__field">
          <dt id="edit-user-name-label" className="label">
            {labels("labelName")}
          </dt>
          <dd aria-labelledby="edit-user-name-label" className="modal__details__value">
            {formData.name || "-"}
          </dd>
        </div>
      </dl>
      <SelectComponent id={"role"} label={labels("labelRole")} name={"role"} options={USER_ROLES.map(role => ({
      value: role.value,
      label: options(role.label)
    }))} value={formData.role} onChange={handleChange} error={errors.role && errors.role[0]} isRequired={true} className="select--full" />
      <section className="input__group">
        {/*
          Etiqueta visible asociada al toggle vía `aria-labelledby` para que
          el nombre accesible del control sea "Doble factor de autenticación"
          (WCAG 1.3.1 / 4.1.2). Se omite la prop `label` interna del toggle
          para evitar un nombre redundante ("Activado"/"Desactivado").
         */}
        <span id="edit-user-2fa-label" className="label">
          {labels("label2FA")}
        </span>
        <ToggleComponent id="twoFactorCode" name="twoFactorCode" checked={formData.twoFactorCode ?? false} aria-labelledby="edit-user-2fa-label" onChange={handleToggleChange} />
      </section>
    </ModalComponent>;
};
export default EditUserModal;